import { v4 as uuidv4 } from 'uuid'
import * as sentry from '../../src/utils/sentry'
import type { Profile } from '../../../app-core/lib/user/Profile'
import { never } from '../../../app-core/lib/util/never'
import type { Post_TFileId } from './Post.file'
import { Post_fromFlatTarget, Post_toFlatTarget } from './Post.target'
import type { Post, ReportSyncPost, SyncPost, Target } from '../../../shared/domain/posts/types.no-deps'
import { PostContent } from '../../../shared/domain/types.no-deps'
import type { NominalUUID, UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { ProcedureEventDto } from '../../../shared/domain/procedureEvents/types.no-deps'
import type { UnitEventDto } from '../../../shared/domain/unitEvents/types.no-deps'
import type { ActionEventDto } from '../../../shared/domain/actionEvents/types.no-deps'

export type Post_TPostId = NominalUUID<'TAppPostId'>
export type Post_TPost = Post_TPostBase & Post_TContent
export type Post_TUiPost = Post_TUiPostBase & Post_TUiContent

export const Post_createPostId = () => uuidv4() as Post_TPostId

export type Post_TContent =
    | Post_TPhotoContent
    | Post_TFileContent
    | Post_TVideoContent
    | Post_TTextContent
    | Post_TReportContent
    | Post_TReportReferenceContent
    | Post_TProcedureEventContent
    | Post_TUnitEventContent
    | Post_TActionEventContent

export type Post_TUiContent =
    | Post_TPhotoContent
    | Post_TFileContent
    | Post_TVideoContent
    | Post_TTextContent
    | Post_TReportContent
    | (Post_TReportReferenceContent & { reportPost?: Post_TUiPost })
    | Post_TProcedureEventContent
    | Post_TUnitEventContent
    | Post_TUiActionEventPost

export type Post_TContentType = Post_TContent['contentType']

export type Post_TReportPost = Post_TPostBase & Post_TReportContent

export type Post_TUiFilePost = Post_TUiPostBase & Post_TFileContent
export type Post_TUiImagePost = Post_TUiPostBase & Post_TPhotoContent
export type Post_TUiReportPost = Post_TUiPostBase & Post_TReportContent
export type Post_TUiTextPost = Post_TUiPostBase & Post_TTextContent
export type Post_TUiVideoPost = Post_TUiPostBase & Post_TVideoContent
export type Post_TUiProcedureEventPost = Post_TUiPostBase & Post_TProcedureEventContent
export type Post_TUiUnitEventPost = Post_TUiPostBase & Post_TUnitEventContent
export type Post_TUiActionEventPost = Post_TUiPostBase & Post_TActionEventContent

export type Post_TUiFilePostUploaded = Post_TUiPostBase & Post_TFileContentUploaded
export type Post_TUiImagePostUploaded = Post_TUiPostBase & Post_TPhotoContentUploaded

export type Post_TUiPostBase = {
    id: Post_TPostId
    target: Target
    createdAt: Date
    createdBy: Profile
    lastModifiedAt: Date
    lastModifiedBy: Profile
    lastModifiedType: 'created' | 'updated' | 'deleted' | 'commented'
    isDeleted: boolean
    commentCount: number
}

export type Post_TPostBase = {
    id: Post_TPostId
    unit: number
    location: number
    workflow: number
    procedure: number
    createdAt: Date
    createdBy: number
    lastModifiedAt: Date
    lastModifiedBy: number
    lastModifiedType: 'created' | 'updated' | 'deleted' | 'commented'
    serverSyncTime: Date
    isDeleted: boolean
    commentCount: number
}

export type Post_TFileContent = Post_TFileContentUploading | Post_TFileContentUploaded

export type Post_TFileContentUploading = {
    contentType: 'file'
    title: string
    fileSizeInBytes: number
    mimeType: string
} & {
    status: 'pendingFileUpload'
    fileId: Post_TFileId
}

export type Post_TFileContentUploaded = {
    contentType: 'file'
    title: string
    fileSizeInBytes: number
    mimeType: string
} & {
    status: 'pendingSync' | 'synced' | 'syncFailed'
    url: string
}

export type Post_TPhotoContent = Post_TPhotoContentUploading | Post_TPhotoContentUploaded

export type Post_TPhotoContentUploading = {
    contentType: 'photo'
    title: string
    fileSizeInBytes: number
} & {
    status: 'pendingFileUpload'
    fileId: Post_TFileId
}

export type Post_TPhotoContentUploaded = {
    contentType: 'photo'
    title: string
    fileSizeInBytes: number
} & {
    status: 'pendingSync' | 'synced' | 'syncFailed'
    url: string
    thumbnail: string
}

export type Post_TProcedureEventContent = {
    status: 'pendingSync' | 'synced' | 'syncFailed'
    contentType: 'procedureEvent'
    procedureEvent: number
    procedureEventData: ProcedureEventDto
}

export type Post_TUnitEventContent = {
    status: 'pendingSync' | 'synced' | 'syncFailed'
    contentType: 'unitEvent'
    unitEvent: number
    unitEventData: UnitEventDto
}

export type Post_TActionEventContent = {
    status: 'pendingSync' | 'synced' | 'syncFailed'
    contentType: 'actionEvent'
    actionEvent: number
    actionEventData: ActionEventDto
}

export type Post_TReportContent = {
    contentType: 'report'
    reportClientId: UUID
    reportParentClientId?: UUID
    reportActivities: number
    reportWorkflowName: string
    reportTitle?: string
    reportStatus?: number
    reportWorkflowId?: number
    reportIsConfidential: boolean
    reportActionsCreated?: number
    reportActionsCompleted?: number
    inProgress?: boolean
} & ({ status: 'synced' | 'syncFailed' | 'pendingSync'; report: number } | { status: 'pendingReportUpload' })

export type Post_TTextContent = {
    status: 'pendingSync' | 'synced' | 'syncFailed'
    contentType: 'text'
    text: string
    language?: string
}

export type Post_TVideoContent = {
    contentType: 'video'
    title: string
    fileSizeInBytes: number
    processingStatus: 'processing' | 'error' | 'success'
} & (
    | {
          status: 'pendingFileUpload'
          fileId: Post_TFileId
      }
    | {
          status: 'pendingSync' | 'synced' | 'syncFailed'
          url: string
          thumbnail: string
      }
)

export type Post_TReportReferenceContent = {
    contentType: 'reportReference'
    status: 'synced'
    report: number
    reportClientId?: UUID
    reportParentClientId?: UUID
    reportTitle?: string
    reportStatus?: number
    reportWorkflowId?: number
}

/**
 * Post.appPostsFromSync
 *
 * Maps a synced post from the server to app posts
 * Written out in full to give typescript an easier time
 */
export function Post_appPostFromSync(post: SyncPost): Post_TPost {
    try {
        const base: Post_TPostBase = {
            id: post.id as Post_TPostId,
            ...Post_toFlatTarget(post),
            createdAt: new Date(post.createdAt),
            createdBy: post.createdBy,
            lastModifiedAt: new Date(post.lastModifiedAt),
            lastModifiedBy: post.lastModifiedBy,
            lastModifiedType: post.lastModifiedType,
            serverSyncTime: new Date(post.serverSyncTime),
            isDeleted: post.isDeleted,
            commentCount: post.commentCount,
        }

        switch (post.contentType) {
            case PostContent.ContentType.file:
                return {
                    ...base,
                    status: 'synced',
                    contentType: 'file',
                    mimeType: post.mimeType,
                    title: post.title,
                    url: post.url,
                    fileSizeInBytes: post.fileSizeInBytes,
                }
            case PostContent.ContentType.photo:
                return {
                    ...base,
                    status: 'synced',
                    contentType: 'photo',
                    title: post.title,
                    url: post.url,
                    thumbnail: post.thumbnail,
                    fileSizeInBytes: post.fileSizeInBytes,
                }
            case PostContent.ContentType.report:
                return {
                    ...base,
                    status: 'synced',
                    contentType: 'report',
                    report: post.report,
                    reportActivities: post.reportActivities,
                    reportWorkflowName: post.reportWorkflowName,
                    reportTitle: post.reportTitle,
                    reportClientId: post.reportClientId,
                    reportParentClientId: post.reportParentClientId,
                    reportStatus: post.reportStatus,
                    reportWorkflowId: post.reportWorkflowId,
                    reportIsConfidential: post.reportIsConfidential,
                }
            case PostContent.ContentType.text:
                return {
                    ...base,
                    status: 'synced',
                    contentType: 'text',
                    text: post.text,
                    ...(post.language && { language: post.language }),
                }
            case PostContent.ContentType.video:
                return {
                    ...base,
                    status: 'synced',
                    contentType: 'video',
                    title: post.title,
                    url: post.url,
                    thumbnail: post.thumbnail,
                    fileSizeInBytes: post.fileSizeInBytes,
                    processingStatus: post.processingStatus,
                }
            case PostContent.ContentType.reportReference:
                return {
                    ...base,
                    status: 'synced',
                    contentType: 'reportReference',
                    report: post.report,
                    reportTitle: post.reportTitle,
                    reportClientId: post.reportClientId,
                    reportParentClientId: post.reportParentClientId,
                    reportStatus: post.reportStatus,
                    reportWorkflowId: post.reportWorkflowId,
                }
            case PostContent.ContentType.procedureEvent:
                return {
                    ...base,
                    status: 'synced',
                    contentType: 'procedureEvent',
                    procedureEvent: post.procedureEvent,
                    procedureEventData: post.procedureEventData,
                }
            case PostContent.ContentType.unitEvent:
                return {
                    ...base,
                    status: 'synced',
                    contentType: 'unitEvent',
                    unitEvent: post.unitEvent,
                    unitEventData: post.unitEventData,
                }
            case PostContent.ContentType.actionEvent:
                return {
                    ...base,
                    status: 'synced',
                    contentType: 'actionEvent',
                    actionEvent: post.actionEvent,
                    actionEventData: post.actionEventData,
                }
            default:
                never(post)
                return null
        }
    } catch (error) {
        sentry.simpleError(error)
        return null
    }
}

/**
 * Post.appPostToSync
 *
 * Prepares a post to be synced with the server
 * Written out in full to give typescript an easier time
 */
export function Post_appPostToSync(post: Post_TPost): SyncPost {
    if (post.status !== 'pendingSync') throw new Error('Post is not ready to be synced')

    const base: Post & { commentCount: number } = {
        id: post.id,
        ...Post_fromFlatTarget(post),
        createdAt: post.createdAt,
        createdBy: post.createdBy,
        lastModifiedAt: post.lastModifiedAt,
        lastModifiedBy: post.lastModifiedBy,
        lastModifiedType: post.lastModifiedType,
        serverSyncTime: post.serverSyncTime,
        isDeleted: post.isDeleted,
        commentCount: post.commentCount,
    }

    switch (post.contentType) {
        case 'file':
            return {
                ...base,
                mimeType: post.mimeType,
                contentType: PostContent.ContentType.file,
                title: post.title,
                url: post.url,
                fileSizeInBytes: post.fileSizeInBytes,
            }
        case 'photo':
            return {
                ...base,
                contentType: PostContent.ContentType.photo,
                title: post.title,
                url: post.url,
                thumbnail: post.thumbnail,
                fileSizeInBytes: post.fileSizeInBytes,
            }
        case 'text':
            return {
                ...base,
                contentType: PostContent.ContentType.text,
                text: post.text,
                language: post.language,
            }
        case 'video':
            return {
                ...base,
                contentType: PostContent.ContentType.video,
                title: post.title,
                url: post.url,
                thumbnail: post.thumbnail,
                fileSizeInBytes: post.fileSizeInBytes,
                processingStatus: post.processingStatus,
            }
        case 'report':
            return {
                ...base,
                contentType: PostContent.ContentType.report,
                report: post.report,
                reportActivities: post.reportActivities,
                reportWorkflowName: post.reportWorkflowName,
                reportTitle: post.reportTitle,
                reportClientId: post.reportClientId,
                reportParentClientId: post.reportParentClientId,
            }
        case 'procedureEvent':
            return {
                ...base,
                contentType: PostContent.ContentType.procedureEvent,
                procedureEvent: post.procedureEvent,
                procedureEventData: post.procedureEventData,
            }
        case 'unitEvent':
            return {
                ...base,
                contentType: PostContent.ContentType.unitEvent,
                unitEvent: post.unitEvent,
                unitEventData: post.unitEventData,
            }
        case 'actionEvent':
            return {
                ...base,
                contentType: PostContent.ContentType.actionEvent,
                actionEvent: post.actionEvent,
                actionEventData: post.actionEventData,
            }
        default:
            never(post)
            throw new Error('Unrecognized post contentType')
    }
}

export const Post_reportPostToSyncPost = (post: Post_TPost): ReportSyncPost => {
    if (post.contentType !== 'report') throw new Error('Post is not a report post')
    if (post.status !== 'pendingReportUpload') throw new Error('Post is not ready to be synced')

    return {
        id: post.id,
        ...Post_fromFlatTarget(post),
        createdAt: post.createdAt,
        createdBy: post.createdBy,
        lastModifiedAt: post.lastModifiedAt,
        lastModifiedBy: post.lastModifiedBy,
        lastModifiedType: post.lastModifiedType,
        serverSyncTime: post.serverSyncTime,
        isDeleted: post.isDeleted,
        commentCount: post.commentCount,
        contentType: PostContent.ContentType.report,
        reportActivities: post.reportActivities,
        reportWorkflowName: post.reportWorkflowName,
        reportTitle: post.reportTitle,
        reportClientId: post.reportClientId,
        reportParentClientId: post.reportParentClientId,
    }
}

export const isPostInProgress = (post: Post_TPost): boolean => {
    return post.contentType === 'report' ? post.inProgress : false
}

export const isReportPost = (post: Post_TPost): boolean => post.contentType === 'report'

export const isFilePost = (post: Post_TPost): boolean => ['file', 'photo', 'video'].includes(post.contentType)
